import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import optimizedApi from '../api/optimizedApi';
import { BlurredImage, useImageWithPlaceholder } from '../components/imagePlaceholder';
import imageCompression from 'browser-image-compression';
import { formatDateTime, formatDate, formatBundleRule, getActiveStateLabel, getBundleTypeLabel } from '../utils/dashboardTranslations';

const IMAGE_COMPRESSION_OPTIONS = {
    maxSizeMB: 0.3,
    maxWidthOrHeight: 800,
    useWebWorker: true
};

const MIME_TYPE_EXTENSIONS = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'image/bmp': '.bmp',
    'image/heic': '.heic',
    'image/heif': '.heif'
};

const buildCompressedImageFile = async (file, fallbackBaseName = 'image') => {
    const compressed = await imageCompression(file, IMAGE_COMPRESSION_OPTIONS);
    const originalExtension = file?.name?.match(/\.[^./\\]+$/)?.[0];
    const inferredExtension = MIME_TYPE_EXTENSIONS[compressed.type] || MIME_TYPE_EXTENSIONS[file?.type] || '.jpg';
    const extension = originalExtension || inferredExtension;
    const originalBaseName = file?.name?.replace(/\.[^./\\]+$/, '')?.trim();
    const baseName = originalBaseName || fallbackBaseName;

    return new File([compressed], `${baseName}${extension}`, {
        type: compressed.type || file?.type || 'image/jpeg',
        lastModified: Date.now()
    });
};

function VendorDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [vendorImage, setVendorImage] = useState(''); // used in JSX, keep as is
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState({
        vendor: false,
        products: false,
        categories: false,
        productCategories: false
    });
    const [form, setForm] = useState({ 
        name: '', 
        priceInSYP: '', 
        costPrice: '', 
        discountPrice: '',
        discountPercentage: '',
        discountStartDate: '',
        discountEndDate: '',
        description: '', 
        imageFile: null, 
        selectedCategoryId: '', 
        categoryName: '', 
        categoryImageFile: null,
        deliveryTimeMinutes: ''
    });
    const [editId, setEditId] = useState(null);
    const [percentageChange, setPercentageChange] = useState('');
    const [editVendorForm, setEditVendorForm] = useState({
        name: '',
        location: '',
        governorate: '',
        city: '',
        latitude: null,
        longitude: null,
        dollarExchangeRate: '',
        vendorCategoryId: '',
        imageFile: null,
        openingTime: '',
        closingTime: '',
        deliveryTimeMinutes: '',
        discountPercentage: '',
        discountStartDate: '',
        discountEndDate: '',
        phoneNumber: '',
        password: ''
    });
    const [categories, setCategories] = useState([]);
    const [productCategories, setProductCategories] = useState([]);
    const [showEditVendor, setShowEditVendor] = useState(false);

    // --- Bundle Deals ---
    const [bundleDeals, setBundleDeals] = useState([]);

    // --- Product Category Edit/Delete ---
    const [catEditId, setCatEditId] = useState(null);
    const [catEditForm, setCatEditForm] = useState({ name: '', imageUrl: '', imageFile: null });

    // --- Weekly Schedule ---
    const [schedule, setSchedule] = useState([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleMessage, setScheduleMessage] = useState(null);

    const [clearDiscount, setClearDiscount] = useState(false);
    const [clearVendorDiscount, setClearVendorDiscount] = useState(false);

    // --- Vendor Override ---
    const [vendorOverride, setVendorOverride] = useState(null);
    const [overrideForm, setOverrideForm] = useState({ mode: 'None', reason: '', fromUtc: '', untilUtc: '' });
    const [vendorOverrideMessage, setVendorOverrideMessage] = useState(null);

    useEffect(() => {
        // Clear product cache to ensure fresh data
        optimizedApi.clearVendorCache(id);
        fetchVendor();
        fetchProducts();
        fetchCategories();
        fetchProductCategories();
        fetchBundleDeals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);


    // --- Schedule + Override data fetch ---
    useEffect(() => {
        const DEFAULT_SCHEDULE = [
            { dayOfWeek: 0, dayName: 'Sunday',    isWorkingDay: true,  openTime: '', closeTime: '', isClosedAllDay: false },
            { dayOfWeek: 1, dayName: 'Monday',    isWorkingDay: true,  openTime: '', closeTime: '', isClosedAllDay: false },
            { dayOfWeek: 2, dayName: 'Tuesday',   isWorkingDay: true,  openTime: '', closeTime: '', isClosedAllDay: false },
            { dayOfWeek: 3, dayName: 'Wednesday', isWorkingDay: true,  openTime: '', closeTime: '', isClosedAllDay: false },
            { dayOfWeek: 4, dayName: 'Thursday',  isWorkingDay: true,  openTime: '', closeTime: '', isClosedAllDay: false },
            { dayOfWeek: 5, dayName: 'Friday',    isWorkingDay: false, openTime: '', closeTime: '', isClosedAllDay: true  },
            { dayOfWeek: 6, dayName: 'Saturday',  isWorkingDay: false, openTime: '', closeTime: '', isClosedAllDay: true  },
        ];

        const fetchSchedule = async () => {
            setScheduleLoading(true);
            try {
                const res = await api.get(`/vendor-availability/${id}/schedule`);
                const rows = Array.isArray(res.data) ? res.data : [];
                if (rows.length === 0) {
                    setSchedule(DEFAULT_SCHEDULE);
                } else {
                    const mapped = DEFAULT_SCHEDULE.map(def => {
                        const found = rows.find(r => r.dayOfWeek === def.dayOfWeek);
                        if (!found) return def;
                        return {
                            dayOfWeek:      found.dayOfWeek,
                            dayName:        def.dayName,
                            isWorkingDay:   found.isWorkingDay   ?? def.isWorkingDay,
                            openTime:       found.openTime       ? found.openTime.substring(0, 5) : '',
                            closeTime:      found.closeTime      ? found.closeTime.substring(0, 5) : '',
                            isClosedAllDay: found.isClosedAllDay ?? def.isClosedAllDay,
                        };
                    });
                    setSchedule(mapped);
                }
            } catch (err) {
                console.error('Error fetching schedule:', err);
                setSchedule(DEFAULT_SCHEDULE);
            } finally {
                setScheduleLoading(false);
            }
        };

        const fetchOverride = async () => {
            try {
                const res = await api.get(`/vendor-availability/${id}/override`);
                const data = res.data;
                if (data && data.mode && data.mode !== 'None') {
                    setVendorOverride(data);
                    setOverrideForm({
                        mode:     data.mode     || 'None',
                        reason:   data.reason   || '',
                        fromUtc:  data.fromUtc  ? data.fromUtc.substring(0, 16) : '',
                        untilUtc: data.untilUtc ? data.untilUtc.substring(0, 16) : '',
                    });
                } else {
                    setVendorOverride(null);
                    setOverrideForm({ mode: 'None', reason: '', fromUtc: '', untilUtc: '' });
                }
            } catch (err) {
                console.error('Error fetching vendor override:', err);
                setVendorOverride(null);
            }
        };

        fetchSchedule();
        fetchOverride();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // --- Schedule handlers ---
    const handleScheduleChange = (dayOfWeek, field, value) => {
        setSchedule(prev => prev.map(day =>
            day.dayOfWeek === dayOfWeek ? { ...day, [field]: value } : day
        ));
    };

    const handleSaveSchedule = async () => {
        setScheduleMessage(null);
        try {
            await api.put(`/vendor-availability/${id}/schedule`, schedule);
            setScheduleMessage({ type: 'success', text: 'تم حفظ الجدول الأسبوعي بنجاح' });
        } catch (err) {
            console.error('Error saving schedule:', err);
            setScheduleMessage({ type: 'danger', text: 'فشل في حفظ الجدول الأسبوعي' });
        }
    };

    // --- Override handlers ---
    const handleApplyOverride = async () => {
        setVendorOverrideMessage(null);
        try {
            const payload = {
                mode:     overrideForm.mode,
                reason:   overrideForm.reason,
                fromUtc:  overrideForm.fromUtc ? new Date(overrideForm.fromUtc).toISOString() : null,
                untilUtc: overrideForm.untilUtc ? new Date(overrideForm.untilUtc).toISOString() : null,
            };
            const res = await api.put(`/vendor-availability/${id}/override`, payload);
            const data = res.data;
            if (data && data.mode && data.mode !== 'None') {
                setVendorOverride(data);
            } else {
                setVendorOverride(overrideForm.mode !== 'None' ? payload : null);
            }
            setVendorOverrideMessage({ type: 'success', text: 'تم تطبيق التجاوز بنجاح' });
        } catch (err) {
            console.error('Error applying override:', err);
            setVendorOverrideMessage({ type: 'danger', text: 'فشل في تطبيق التجاوز' });
        }
    };

    const handleClearOverride = async () => {
        setVendorOverrideMessage(null);
        try {
            await api.delete(`/vendor-availability/${id}/override`);
            setVendorOverride(null);
            setOverrideForm({ mode: 'None', reason: '', fromUtc: '', untilUtc: '' });
            setVendorOverrideMessage({ type: 'success', text: 'تم إلغاء التجاوز بنجاح' });
        } catch (err) {
            console.error('Error clearing override:', err);
            setVendorOverrideMessage({ type: 'danger', text: 'فشل في إلغاء التجاوز' });
        }
    };

    const fetchVendor = useCallback(async (forceRefresh = false) => {
        setLoading(prev => ({ ...prev, vendor: true }));
        try {
            console.log(`Fetching vendor details for ID: ${id}${forceRefresh ? ' (force refresh)' : ''}`);
            
            if (forceRefresh) {
                optimizedApi.clearVendorCache(id);
            }
            
            // Add timestamp to force fresh data when needed
            const url = forceRefresh ? `/vendors/${id}?t=${Date.now()}` : `/vendors/${id}`;
            const res = await (forceRefresh ? api.get(url) : optimizedApi.getVendor(id));
            
            console.log('Vendor response:', res.data);
            console.log('Vendor time fields:', {
                openingTime: res.data.openingTime,
                closingTime: res.data.closingTime,
                deliveryTimeMinutes: res.data.deliveryTimeMinutes
            });
            setVendor(res.data);
            setVendorImage(res.data.imageUrl || '');
        } catch (error) {
            console.error('Error fetching vendor:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
        } finally {
            setLoading(prev => ({ ...prev, vendor: false }));
        }
    }, [id]);

    const fetchProducts = useCallback(async () => {
        setLoading(prev => ({ ...prev, products: true }));
        try {
            console.log(`Fetching products for vendor ID: ${id}`);
            const res = await optimizedApi.getVendorProducts(id);
            console.log('Products response:', res.data);
            if (res.data && res.data.length > 0) {
                console.log('First product:', res.data[0]);
                console.log('First product description:', res.data[0].description);
            }
            setProducts(res.data || []);
        } catch (error) {
            console.error('Error fetching products:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            setProducts([]);
        } finally {
            setLoading(prev => ({ ...prev, products: false }));
        }
    }, [id]);

    const fetchCategories = useCallback(async () => {
        setLoading(prev => ({ ...prev, categories: true }));
        try {
            const res = await optimizedApi.getVendorCategories();
            console.log('Vendor categories response:', res.data);
            
            // Handle different response structures
            if (Array.isArray(res.data)) {
                setCategories(res.data);
            } else if (res.data.categories && Array.isArray(res.data.categories)) {
                setCategories(res.data.categories);
            } else {
                console.error('Unexpected vendor categories response structure:', res.data);
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching vendor categories:', error);
            setCategories([]);
        } finally {
            setLoading(prev => ({ ...prev, categories: false }));
        }
    }, []);

    const fetchProductCategories = useCallback(async () => {
        setLoading(prev => ({ ...prev, productCategories: true }));
        try {
            console.log(`Fetching product categories for vendor ID: ${id}`);
            const res = await optimizedApi.getVendorProductCategories(id);
            console.log('Vendor product categories response:', res.data);
            
            // Handle different response structures
            if (Array.isArray(res.data)) {
                setProductCategories(res.data);
            } else if (res.data && res.data.categories && Array.isArray(res.data.categories)) {
                setProductCategories(res.data.categories);
            } else {
                console.error('Unexpected product categories response structure:', res.data);
                setProductCategories([]);
            }
        } catch (error) {
            console.error('Error fetching vendor product categories:', error);
            setProductCategories([]);
        } finally {
            setLoading(prev => ({ ...prev, productCategories: false }));
        }
    }, [id]);

    // --- Product Form ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('=== VENDOR DETAILS PRODUCT SUBMIT DEBUG ===');
        console.log('Vendor ID:', id);
        console.log('Edit ID:', editId);
        console.log('Form data before submission:', {
            name: form.name,
            priceInSYP: form.priceInSYP,
            costPrice: form.costPrice,
            discountPrice: form.discountPrice,
            discountPercentage: form.discountPercentage,
            description: form.description,
            selectedCategoryId: form.selectedCategoryId,
            categoryName: form.categoryName,
            deliveryTimeMinutes: form.deliveryTimeMinutes,
            hasImageFile: !!form.imageFile,
            hasCategoryImageFile: !!form.categoryImageFile
        });

        if (form.discountPrice && form.discountPercentage) {
            alert('لا يمكن إدخال سعر خصم ونسبة خصم معاً.');
            return;
        }
        const basePrice = Number(form.priceInSYP || 0);
        const discountPrice = Number(form.discountPrice || 0);
        const discountPercentage = Number(form.discountPercentage || 0);
        if (form.discountPrice && discountPrice >= basePrice) {
            alert('سعر الخصم يجب أن يكون أقل من السعر الأساسي.');
            return;
        }
        if (form.discountPercentage && (discountPercentage <= 0 || discountPercentage >= 100)) {
            alert('نسبة الخصم يجب أن تكون بين 1 و 99.');
            return;
        }
        if (form.discountStartDate && form.discountEndDate && new Date(form.discountEndDate) <= new Date(form.discountStartDate)) {
            alert('تاريخ نهاية الخصم يجب أن يكون بعد تاريخ البداية.');
            return;
        }
        
        const formData = new FormData();
        formData.append('Name', form.name);
        formData.append('PriceInSYP', form.priceInSYP);
        formData.append('CostPrice', form.costPrice);
        formData.append('Description', form.description || '');
        if (form.imageFile) {
            const compressed = await buildCompressedImageFile(form.imageFile, 'product-image');
            formData.append('Image', compressed, compressed.name);
        }
        if (form.deliveryTimeMinutes) {
            formData.append('DeliveryTimeMinutes', form.deliveryTimeMinutes);
        }
        if (clearDiscount) {
            formData.append('ClearDiscount', 'true');
        } else {
            if (form.discountPrice) {
                formData.append('DiscountPrice', form.discountPrice);
            }
            if (form.discountPercentage) {
                formData.append('DiscountPercentage', form.discountPercentage);
            }
            if (form.discountStartDate) {
                formData.append('DiscountStartDate', form.discountStartDate);
            }
            if (form.discountEndDate) {
                formData.append('DiscountEndDate', form.discountEndDate);
            }
        }
        // Category logic
        if (form.selectedCategoryId === 'new') {
            console.log('Creating new category:', form.categoryName);
            if (form.categoryName) formData.append('CategoryName', form.categoryName);
            if (form.categoryImageFile) {
                const compressedCat = await buildCompressedImageFile(form.categoryImageFile, 'category-image');
                formData.append('CategoryImage', compressedCat, compressedCat.name);
            }
            if (id) formData.append('VendorId', id); // <-- Always provide vendorId for new category
        } else if (form.selectedCategoryId) {
            console.log('Using existing category ID:', form.selectedCategoryId);
            formData.append('ProductCategoryId', form.selectedCategoryId);
        } else {
            console.log('WARNING: No category selected!');
        }
        
        // Debug FormData contents
        console.log('FormData contents:');
        for (let [key, value] of formData.entries()) {
            console.log(`  ${key}:`, value);
        }
        
        try {
            if (editId) {
                console.log(`Making PUT request to: /vendors/${id}/products/${editId}/with-image`);
                const response = await optimizedApi.put(`/vendors/${id}/products/${editId}/with-image`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                console.log('Update response:', response.data);
            } else {
                console.log(`Making POST request to: /vendors/${id}/products/with-image`);
                await optimizedApi.post(`/vendors/${id}/products/with-image`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            setForm({
                name: '',
                priceInSYP: '',
                costPrice: '',
                discountPrice: '',
                discountPercentage: '',
                discountStartDate: '',
                discountEndDate: '',
                description: '',
                imageFile: null,
                selectedCategoryId: '',
                categoryName: '',
                categoryImageFile: null,
                deliveryTimeMinutes: ''
            });
            setEditId(null);
            setClearDiscount(false);
            fetchProducts();
            console.log('Product save successful');
            if (document.getElementById('product-image-input')) {
                document.getElementById('product-image-input').value = '';
            }
            if (document.getElementById('category-image-input')) {
                document.getElementById('category-image-input').value = '';
            }
        } catch {
            alert('فشل في حفظ المنتج');
        }
    };

    const handleEdit = (product) => {
        console.log('=== VENDOR DETAILS PRODUCT EDIT DEBUG ===');
        console.log('Product being edited:', product);
        console.log('Product category info:', {
            productCategoryId: product.productCategoryId,
            productCategory: product.productCategory,
            hasProductCategory: !!product.productCategory
        });
        console.log('Available product categories:', productCategories);
        
        setForm({
            name: product.name,
            priceInSYP: product.priceInSYP,
            costPrice: product.costPrice,
            discountPrice: product.discountPrice ?? '',
            discountPercentage: product.discountPercentage ?? '',
            discountStartDate: product.discountStartDate ? String(product.discountStartDate).slice(0, 10) : '',
            discountEndDate: product.discountEndDate ? String(product.discountEndDate).slice(0, 10) : '',
            description: product.description || '',
            selectedCategoryId: product.productCategoryId || '',
            imageFile: null, // User must re-select image if changing
            categoryName: '',
            categoryImageFile: null,
            deliveryTimeMinutes: product.deliveryTimeMinutes || ''
        });
        setEditId(product.id);
        setClearDiscount(false);

        console.log('Form set to:', {
            name: product.name,
            selectedCategoryId: product.productCategoryId || '',
            productCategoryId: product.productCategoryId
        });
        
        // Reset file input value
        const fileInput = document.getElementById('product-image-input');
        if (fileInput) fileInput.value = '';
    };

    const handleDeleteVendor = async () => {
        if (window.confirm('هل أنت متأكد من حذف البائع؟')) {
            try {
                const token = localStorage.getItem('token');
                await api.delete(`/Vendor/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                alert('تم حذف البائع بنجاح');
                navigate('/dashboard/vendors');
            } catch (err) {
                console.error(err);
                alert('فشل في حذف البائع');
            }
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (window.confirm('هل تريد حذف هذا المنتج؟')) {
            try {
                await api.delete(`/vendors/${id}/products/${productId}`); // fixed endpoint
                setProducts(products => products.filter(p => p.id !== productId)); // update UI immediately
                // Optionally, still call fetchProducts() to sync with backend
                // fetchProducts();
            } catch {
                alert('فشل في حذف المنتج');
            }
        }
    };

    const handleToggleAvailability = async (product) => {
        try {
            const res = await api.put(`/vendors/${id}/products/${product.id}/toggle-availability`);
            setProducts(prev => prev.map(p =>
                p.id === product.id ? { ...p, isAvailable: res.data.isAvailable } : p
            ));
        } catch (error) {
            console.error('Error toggling availability:', error);
            alert('فشل في تغيير حالة المنتج');
        }
    };

    const handleMassPriceUpdate = async () => {
        const percentage = parseFloat(percentageChange);
        if (isNaN(percentage)) {
            alert('الرجاء إدخال نسبة مئوية صحيحة');
            return;
        }
        try {
            await api.put(`Vendor/${id}/update-product-prices`, { percentage });
            alert('تم تحديث الأسعار بنجاح');
            fetchProducts();
        } catch (err) {
            console.error('Price update error:', err.response?.data || err);
            alert('فشل في تحديث الأسعار');
        }
    };

    const openEditVendor = () => {
        if (vendor) {
            console.log('🔧 Opening vendor edit form with data:', {
                openingTime: vendor.openingTime,
                closingTime: vendor.closingTime,
                deliveryTimeMinutes: vendor.deliveryTimeMinutes,
                rawVendorData: vendor
            });
            
            // Format time values for HTML time input (HH:mm format)
            const formatTimeForInput = (timeValue) => {
                if (!timeValue) return '';
                
                // If it's already in HH:mm format, return as is
                if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}$/)) {
                    return timeValue;
                }
                
                // If it's in HH:mm:ss format, remove seconds
                if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}:\d{2}/)) {
                    return timeValue.substring(0, 5);
                }
                
                return timeValue;
            };

            setEditVendorForm({
                name: vendor.name || '',
                location: vendor.location || '',
                governorate: vendor.governorate || '',
                city: vendor.city || '',
                latitude: vendor.latitude ?? null,
                longitude: vendor.longitude ?? null,
                dollarExchangeRate: vendor.dollarExchangeRate || '',
                vendorCategoryId: vendor.vendorCategoryId || '',
                imageFile: null,
                openingTime: formatTimeForInput(vendor.openingTime),
                closingTime: formatTimeForInput(vendor.closingTime),
                deliveryTimeMinutes: vendor.deliveryTimeMinutes || '',
                discountPercentage: vendor.discountPercentage ?? '',
                discountStartDate: vendor.discountStartDate ? String(vendor.discountStartDate).slice(0, 10) : '',
                discountEndDate: vendor.discountEndDate ? String(vendor.discountEndDate).slice(0, 10) : '',
                phoneNumber: vendor.phoneNumber || '',
                password: '' // Keep password empty for security
            });
            
            console.log('✅ Edit form initialized with values:', {
                openingTime: formatTimeForInput(vendor.openingTime),
                closingTime: formatTimeForInput(vendor.closingTime),
                deliveryTimeMinutes: vendor.deliveryTimeMinutes || ''
            });
            
            setClearVendorDiscount(false);
            setShowEditVendor(true);
        }
    };

    // --- Vendor Edit Form ---
    const handleEditVendorChange = async (e) => {
        const { name, value, files } = e.target;
        if (name === 'imageFile') {
            if (files && files[0]) {
                const compressed = await buildCompressedImageFile(files[0], 'vendor-image');
                setEditVendorForm(f => ({ ...f, imageFile: compressed }));
            }
        } else {
            setEditVendorForm(f => ({ ...f, [name]: value }));
        }
    };

    const handleEditVendorSubmit = async (e) => {
        e.preventDefault();
        console.log('🚀 Submitting vendor edit with form data:', editVendorForm);
        
        const formData = new FormData();
        formData.append('name', editVendorForm.name);
        formData.append('location', editVendorForm.location);
        formData.append('governorate', editVendorForm.governorate);
        formData.append('city', editVendorForm.city);
        if (editVendorForm.latitude !== null && editVendorForm.latitude !== '') formData.append('Latitude', editVendorForm.latitude);
        if (editVendorForm.longitude !== null && editVendorForm.longitude !== '') formData.append('Longitude', editVendorForm.longitude);
        formData.append('dollarExchangeRate', editVendorForm.dollarExchangeRate);
        formData.append('vendorCategoryId', editVendorForm.vendorCategoryId);
        if (editVendorForm.openingTime) {
            formData.append('OpeningTime', editVendorForm.openingTime);
            console.log('📤 Sending OpeningTime:', editVendorForm.openingTime);
        }
        if (editVendorForm.closingTime) {
            formData.append('ClosingTime', editVendorForm.closingTime);
            console.log('📤 Sending ClosingTime:', editVendorForm.closingTime);
        }
        if (editVendorForm.deliveryTimeMinutes) {
            formData.append('DeliveryTimeMinutes', editVendorForm.deliveryTimeMinutes);
            console.log('📤 Sending DeliveryTimeMinutes:', editVendorForm.deliveryTimeMinutes);
        }
        if (clearVendorDiscount) {
            formData.append('ClearDiscount', 'true');
        } else {
            if (editVendorForm.discountPercentage !== '') {
                formData.append('DiscountPercentage', editVendorForm.discountPercentage);
                console.log('📤 Sending DiscountPercentage:', editVendorForm.discountPercentage);
            }
            if (editVendorForm.discountStartDate) {
                formData.append('DiscountStartDate', editVendorForm.discountStartDate);
                console.log('📤 Sending DiscountStartDate:', editVendorForm.discountStartDate);
            }
            if (editVendorForm.discountEndDate) {
                formData.append('DiscountEndDate', editVendorForm.discountEndDate);
                console.log('📤 Sending DiscountEndDate:', editVendorForm.discountEndDate);
            }
        }
        if (editVendorForm.phoneNumber) {
            formData.append('PhoneNumber', editVendorForm.phoneNumber);
            console.log('📤 Sending PhoneNumber:', editVendorForm.phoneNumber);
        }
        if (editVendorForm.password) {
            formData.append('Password', editVendorForm.password);
            console.log('📤 Sending Password: [HIDDEN]');
        }
        if (editVendorForm.imageFile) formData.append('image', editVendorForm.imageFile, editVendorForm.imageFile.name);
        
        // Debug: Log all FormData entries
        for (let pair of formData.entries()) {
            console.log(`📋 FormData: ${pair[0]} = ${pair[1]}`);
        }
        
        try {
            console.log(`📡 Making PUT request to: /Vendor/${id}/with-image`);
            const response = await api.put(`/Vendor/${id}/with-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            console.log('✅ Vendor update response:', response.data);
            
            // Clear the vendor cache to ensure fresh data
            optimizedApi.clearVendorCache(id);
            
            setShowEditVendor(false);
            setClearVendorDiscount(false);

            // Fetch fresh vendor data with force refresh
            await fetchVendor(true);
            
            console.log('✅ Vendor data refreshed after edit');
            
        } catch (error) {
            console.error('❌ Vendor update error:', error);
            console.error('Error response:', error.response?.data);
            alert('فشل في تعديل بيانات البائع');
        }
    };

    // Helper to get full image URL - memoized for better performance
    const getImageUrl = useMemo(() => {
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api', '');
        return (url) => {
            if (!url) return '';
            if (url.startsWith('http://') || url.startsWith('https://')) return url.replace(':5000', '');
            return apiBase + url;
        };
    }, []);

    // Helper to create fallback URLs
    const getFallbackUrl = useMemo(() => {
        return (url) => {
            if (!url) return '';
            if (url.startsWith('http://') || url.startsWith('https://')) return url;
            
            // Get the base URL from environment variable
            const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api', '');
            
            // If imageUrl starts with /uploads or /wwwroot, use it directly
            if (url.startsWith('/uploads') || url.startsWith('/wwwroot')) {
                return `${apiBase}${url}`;
            }
            
            // If it's just a filename, assume it's in uploads folder
            if (!url.startsWith('/')) {
                return `${apiBase}/uploads/${url}`;
            }
            
            return `${apiBase}${url}`;
        };
    }, []);


    // --- Product Category Edit/Delete ---
    const handleCatEdit = (cat) => {
        setCatEditId(cat.id);
        setCatEditForm({ name: cat.name, imageUrl: cat.imageUrl, imageFile: null });
    };

    const handleCatDelete = async (id) => {
        if (window.confirm('هل تريد حذف الفئة؟')) {
            try {
                await api.delete(`/ProductCategory/${id}`);
                setCatEditId(null);
                setCatEditForm({ name: '', imageUrl: '', imageFile: null });
                fetchProductCategories();
            } catch {
                alert('فشل في حذف الفئة');
            }
        }
    };

    const handleCatMoveUp = async (catId) => {
        try {
            await api.patch(`/ProductCategory/${catId}/move-up`);
            optimizedApi.clearVendorCache(id);
            fetchProductCategories();
        } catch (err) {
            if (err?.response?.status !== 400) {
                alert('فشل تغيير الترتيب');
            }
        }
    };

    const handleCatMoveDown = async (catId) => {
        try {
            await api.patch(`/ProductCategory/${catId}/move-down`);
            optimizedApi.clearVendorCache(id);
            fetchProductCategories();
        } catch (err) {
            if (err?.response?.status !== 400) {
                alert('فشل تغيير الترتيب');
            }
        }
    };

    // --- Category Edit Image ---
    const handleCatEditImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const compressed = await buildCompressedImageFile(file, 'category-image');
        setCatEditForm(f => ({ ...f, imageFile: compressed }));
        const formData = new FormData();
        formData.append('Image', compressed, compressed.name);
        try {
            const res = await api.post('/Upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setCatEditForm(f => ({ ...f, imageUrl: res.data.url || res.data.imageUrl }));
        } catch {
            alert('فشل رفع الصورة');
        }
    };

    const handleCatEditSubmit = async (e) => {
        e.preventDefault();
        let imageUrl = catEditForm.imageUrl;
        if (catEditForm.imageFile && !catEditForm.imageUrl) {
            const formData = new FormData();
            formData.append('Image', catEditForm.imageFile, catEditForm.imageFile.name);
            const res = await api.post('/Upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            imageUrl = res.data.url || res.data.imageUrl;
        }
        try {
            await api.put(`/ProductCategory/${catEditId}`, {
                name: catEditForm.name,
                imageUrl,
                vendorId: id
            });
            setCatEditId(null);
            setCatEditForm({ name: '', imageUrl: '', imageFile: null });
            fetchProductCategories();
        } catch {
            alert('فشل في تعديل الفئة');
        }
    };

    const fetchBundleDeals = useCallback(async () => {
        try {
            const res = await api.get(`/admin/bundle-deals?vendorId=${id}&limit=100`);
            setBundleDeals(res.data?.items || []);
        } catch (error) {
            console.error('Error fetching bundle deals:', error);
        }
    }, [id]);

    const handleDeactivateDeal = async (dealId) => {
        if (!window.confirm('هل تريد تعطيل هذا العرض؟')) return;
        try {
            await api.delete(`/admin/bundle-deals/${dealId}`);
            fetchBundleDeals();
        } catch (error) {
            console.error('Error deactivating bundle deal:', error);
            alert('فشل تعطيل العرض');
        }
    };

    return (
        <div>
            <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/dashboard/vendors')}>
                ← الرجوع لقائمة البائعين
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDeleteVendor}>
                🗑️ حذف
            </button>

            {/* Edit Vendor Modal or Section */}
            {showEditVendor && (
                <form className="mb-4 p-3 border rounded" onSubmit={handleEditVendorSubmit} style={{ background: '#f9f9f9' }}>
                    <h5 className="mb-3">تعديل بيانات البائع</h5>
                    <div className="row">
                        <div className="col-md-3">
                            <input
                                className="form-control mb-2"
                                name="name"
                                placeholder="اسم البائع"
                                value={editVendorForm.name}
                                onChange={handleEditVendorChange}
                                required
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                className="form-control mb-2"
                                name="location"
                                placeholder="الموقع"
                                value={editVendorForm.location}
                                onChange={handleEditVendorChange}
                                required
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                className="form-control mb-2"
                                name="governorate"
                                placeholder="المحافظة"
                                value={editVendorForm.governorate}
                                onChange={handleEditVendorChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                className="form-control mb-2"
                                name="city"
                                placeholder="المدينة"
                                value={editVendorForm.city}
                                onChange={handleEditVendorChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                type="number"
                                step="any"
                                className="form-control mb-2"
                                name="latitude"
                                placeholder="خط العرض (Latitude) مثال: 33.510"
                                value={editVendorForm.latitude ?? ''}
                                onChange={e => setEditVendorForm(f => ({ ...f, latitude: e.target.value ? parseFloat(e.target.value) : null }))}
                            />
                            <label className="form-label text-muted">خط العرض (Latitude)</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="number"
                                step="any"
                                className="form-control mb-2"
                                name="longitude"
                                placeholder="خط الطول (Longitude) مثال: 36.291"
                                value={editVendorForm.longitude ?? ''}
                                onChange={e => setEditVendorForm(f => ({ ...f, longitude: e.target.value ? parseFloat(e.target.value) : null }))}
                            />
                            <label className="form-label text-muted">خط الطول (Longitude)</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="number"
                                className="form-control mb-2"
                                name="dollarExchangeRate"
                                placeholder="سعر صرف الدولار"
                                value={editVendorForm.dollarExchangeRate}
                                onChange={handleEditVendorChange}
                                required
                            />
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-control mb-2"
                                name="vendorCategoryId"
                                value={editVendorForm.vendorCategoryId}
                                onChange={handleEditVendorChange}
                                required
                            >
                                <option value="">اختر الفئة</option>
                                {Array.isArray(categories) && categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="time"
                                className="form-control mb-2"
                                name="openingTime"
                                placeholder="وقت الافتتاح"
                                value={editVendorForm.openingTime}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">وقت الافتتاح</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="time"
                                className="form-control mb-2"
                                name="closingTime"
                                placeholder="وقت الإغلاق"
                                value={editVendorForm.closingTime}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">وقت الإغلاق</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="number"
                                className="form-control mb-2"
                                name="deliveryTimeMinutes"
                                placeholder="وقت التوصيل (دقيقة)"
                                value={editVendorForm.deliveryTimeMinutes}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">وقت التوصيل (دقيقة)</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="number"
                                className="form-control mb-2"
                                name="discountPercentage"
                                placeholder="نسبة الخصم %"
                                min="0"
                                max="99"
                                value={editVendorForm.discountPercentage}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">خصم المتجر (%)</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="date"
                                className="form-control mb-2"
                                name="discountStartDate"
                                value={editVendorForm.discountStartDate}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">بداية الخصم</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="date"
                                className="form-control mb-2"
                                name="discountEndDate"
                                value={editVendorForm.discountEndDate}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">نهاية الخصم</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="file"
                                accept="image/*"
                                className="form-control mb-2"
                                name="imageFile"
                                onChange={handleEditVendorChange}
                            />
                            {/* Show current image if exists */}
                            {vendor && vendor.imageUrl && (
                                <BlurredImage
                                    src={getImageUrl(vendor.imageUrl)}
                                    alt="Vendor"
                                    style={{ width: 80, marginTop: 5, borderRadius: 6 }}
                                    fallbackUrl={getFallbackUrl(vendor.imageUrl)}
                                />
                            )}
                        </div>
                        <div className="col-md-3">
                            <input
                                className="form-control mb-2"
                                name="phoneNumber"
                                placeholder="رقم الهاتف"
                                value={editVendorForm.phoneNumber}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">رقم الهاتف لتسجيل الدخول</label>
                        </div>
                        <div className="col-md-3">
                            <input
                                type="password"
                                className="form-control mb-2"
                                name="password"
                                placeholder="كلمة المرور الجديدة"
                                value={editVendorForm.password}
                                onChange={handleEditVendorChange}
                            />
                            <label className="form-label text-muted">كلمة المرور (اتركها فارغة للاحتفاظ بالحالية)</label>
                        </div>
                    </div>
                    <button className="btn btn-success me-2" type="submit">حفظ التعديلات</button>
                    <button className="btn btn-secondary me-2" type="button" onClick={() => { setShowEditVendor(false); setClearVendorDiscount(false); }}>إلغاء</button>
                    <button
                        className="btn btn-warning"
                        type="button"
                        onClick={() => {
                            setEditVendorForm(f => ({ ...f, discountPercentage: '', discountStartDate: '', discountEndDate: '' }));
                            setClearVendorDiscount(true);
                        }}
                    >
                        حذف عرض المتجر
                    </button>
                </form>
            )}

            {vendor && (
                <div className="mb-4">
                    <h3>{vendor.name}</h3>
                    <p>الموقع: {vendor.location}</p>
                    <p>المحافظة: {vendor.governorate || '—'}</p>
                    <p>المدينة: {vendor.city || '—'}</p>
                    <p>الفئة: {vendor.vendorCategory?.name}</p>
                    {vendor.openingTime && (
                        <p>وقت الافتتاح: <strong style={{color: 'green'}}>{vendor.openingTime}</strong></p>
                    )}
                    {vendor.closingTime && (
                        <p>وقت الإغلاق: <strong style={{color: 'red'}}>{vendor.closingTime}</strong></p>
                    )}
                    {vendor.deliveryTimeMinutes && (
                        <p>وقت التوصيل: <strong style={{color: 'blue'}}>{vendor.deliveryTimeMinutes} دقيقة</strong></p>
                    )}
                    {vendor.discountPercentage && (
                        <p>
                            خصم المتجر: <strong style={{ color: '#f7941d' }}>{vendor.discountPercentage}%</strong>
                            {' '}({vendor.discountStartDate ? String(vendor.discountStartDate).slice(0, 10) : '—'} - {vendor.discountEndDate ? String(vendor.discountEndDate).slice(0, 10) : '—'})
                        </p>
                    )}
                    {vendor.imageUrl && (
                        <div style={{ maxWidth: 300 }}>
                            <label className="form-label">صورة البائع</label>
                            <BlurredImage
                                src={getImageUrl(vendor.imageUrl)}
                                alt="Vendor"
                                style={{ width: 200, marginTop: 10, marginBottom: 15, borderRadius: 8, display: 'block' }}
                                fallbackUrl={getFallbackUrl(vendor.imageUrl)}
                            />
                        </div>
                    )}

                    <div className="input-group mb-3" style={{ maxWidth: '300px' }}>
                        <input
                            type="number"
                            className="form-control"
                            placeholder="نسبة التعديل (مثال: -5 أو 10)"
                            value={percentageChange}
                            onChange={(e) => setPercentageChange(e.target.value)}
                        />
                        <button className="btn btn-warning" onClick={handleMassPriceUpdate}>
                            تحديث الأسعار
                        </button>
                    </div>

                    {/* Add an edit button near vendor info */}
                    <button className="btn btn-primary btn-sm mb-2" onClick={openEditVendor}>تعديل بيانات البائع</button>
                </div>
            )}

            {/* ─── Weekly Schedule Editor ─── */}
            <div className="card mb-4">
                <div className="card-header">
                    <h5 className="mb-0">الجدول الأسبوعي لأوقات العمل</h5>
                </div>
                <div className="card-body">
                    {scheduleLoading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border spinner-border-sm" role="status" />
                            <span className="ms-2">جاري التحميل...</span>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-bordered text-center align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>اليوم</th>
                                            <th>يوم عمل</th>
                                            <th>وقت الفتح</th>
                                            <th>وقت الإغلاق</th>
                                            <th>مغلق طوال اليوم</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schedule.map(day => (
                                            <tr key={day.dayOfWeek}>
                                                <td className="fw-semibold">{day.dayName}</td>
                                                <td>
                                                    <div className="form-check d-flex justify-content-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={day.isWorkingDay}
                                                            onChange={e => handleScheduleChange(day.dayOfWeek, 'isWorkingDay', e.target.checked)}
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="time"
                                                        className="form-control form-control-sm"
                                                        value={day.openTime}
                                                        disabled={!day.isWorkingDay || day.isClosedAllDay}
                                                        onChange={e => handleScheduleChange(day.dayOfWeek, 'openTime', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="time"
                                                        className="form-control form-control-sm"
                                                        value={day.closeTime}
                                                        disabled={!day.isWorkingDay || day.isClosedAllDay}
                                                        onChange={e => handleScheduleChange(day.dayOfWeek, 'closeTime', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="form-check d-flex justify-content-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={day.isClosedAllDay}
                                                            disabled={!day.isWorkingDay}
                                                            onChange={e => handleScheduleChange(day.dayOfWeek, 'isClosedAllDay', e.target.checked)}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {scheduleMessage && (
                                <div className={`alert alert-${scheduleMessage.type} py-2`} role="alert">
                                    {scheduleMessage.text}
                                </div>
                            )}
                            <button className="btn btn-primary" onClick={handleSaveSchedule}>
                                حفظ الجدول الأسبوعي
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* ─── Vendor Override (Temporary Close/Open) ─── */}
            <div className="card mb-4">
                <div className="card-header">
                    <h5 className="mb-0">تجاوز مؤقت</h5>
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        {vendorOverride && vendorOverride.mode && vendorOverride.mode !== 'None' ? (
                            <div className={`alert ${vendorOverride.mode === 'ForceOpen' ? 'alert-success' : 'alert-danger'} py-2`}>
                                <strong>
                                    المتجر حالياً:&nbsp;
                                    {vendorOverride.mode === 'ForceOpen' ? 'مفتوح قسراً' : 'مغلق قسراً'}
                                </strong>
                                {vendorOverride.reason && (
                                    <span className="ms-2">— السبب: {vendorOverride.reason}</span>
                                )}
                                {vendorOverride.fromUtc && (
                                    <span className="ms-2">— من: {formatDateTime(vendorOverride.fromUtc)}</span>
                                )}
                                {vendorOverride.untilUtc && (
                                    <span className="ms-2">— حتى: {formatDateTime(vendorOverride.untilUtc)}</span>
                                )}
                            </div>
                        ) : (
                            <div className="alert alert-secondary py-2">
                                لا يوجد تجاوز نشط حالياً
                            </div>
                        )}
                    </div>

                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">الوضع</label>
                            <select
                                className="form-select"
                                value={overrideForm.mode}
                                onChange={e => setOverrideForm(f => ({ ...f, mode: e.target.value }))}
                            >
                                <option value="None">بدون تجاوز</option>
                                <option value="ForceOpen">فتح قسري</option>
                                <option value="ForceClosed">إغلاق قسري</option>
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-semibold">السبب</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="سبب التجاوز (اختياري)"
                                value={overrideForm.reason}
                                onChange={e => setOverrideForm(f => ({ ...f, reason: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">من (تاريخ ووقت)</label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={overrideForm.fromUtc}
                                onChange={e => setOverrideForm(f => ({ ...f, fromUtc: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">حتى (تاريخ ووقت)</label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={overrideForm.untilUtc}
                                onChange={e => setOverrideForm(f => ({ ...f, untilUtc: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-2 d-flex gap-2">
                            <button className="btn btn-warning" onClick={handleApplyOverride}>
                                تطبيق
                            </button>
                            {vendorOverride && vendorOverride.mode && vendorOverride.mode !== 'None' && (
                                <button className="btn btn-outline-danger" onClick={handleClearOverride}>
                                    إلغاء
                                </button>
                            )}
                        </div>
                    </div>

                    {vendorOverrideMessage && (
                        <div className={`alert alert-${vendorOverrideMessage.type} py-2 mt-3`} role="alert">
                            {vendorOverrideMessage.text}
                        </div>
                    )}
                </div>
            </div>

            {/* Bundle Deals Section */}
            <div className="mt-5">
                <h5 className="mb-3">عروض الحزم</h5>
                {bundleDeals.length === 0 ? (
                    <p className="text-muted">لا توجد عروض حزم لهذا البائع.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-bordered text-center align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>الاسم</th>
                                    <th>النوع</th>
                                    <th>القاعدة</th>
                                    <th>من</th>
                                    <th>إلى</th>
                                    <th>الحالة</th>
                                    <th>إجراء</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bundleDeals.map((deal) => (
                                    <tr key={deal.id}>
                                        <td>{deal.name}</td>
                                        <td>{getBundleTypeLabel(deal.bundleType)}</td>
                                        <td>{formatBundleRule(deal)}</td>
                                        <td>{formatDate(deal.startDate)}</td>
                                        <td>{formatDate(deal.endDate)}</td>
                                        <td>
                                            <span className={`badge ${deal.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                {getActiveStateLabel(deal.isActive)}
                                            </span>
                                        </td>
                                        <td>
                                            {deal.isActive && (
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleDeactivateDeal(deal.id)}
                                                >
                                                    تعطيل
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add Category Form */}
            <h5 className="mb-3">إضافة فئة منتج</h5>
            {catEditId ? (
                <form onSubmit={handleCatEditSubmit} className="row mb-4">
                    <div className="col-md-3">
                        <input
                            className="form-control mb-2"
                            placeholder="اسم الفئة"
                            value={catEditForm.name}
                            onChange={e => setCatEditForm(f => ({ ...f, name: e.target.value }))}
                            required
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            type="file"
                            accept="image/*"
                            className="form-control mb-2"
                            onChange={handleCatEditImageChange}
                        />
                        {catEditForm.imageUrl && (
                            <BlurredImage src={getImageUrl(catEditForm.imageUrl)} alt="تصوير" style={{ maxWidth: 80, maxHeight: 80, marginTop: 8 }} fallbackUrl={getFallbackUrl(catEditForm.imageUrl)} />
                        )}
                    </div>
                    <div className="col-md-2">
                        <button className="btn btn-success">تعديل</button>
                        <button type="button" className="btn btn-secondary ms-2" onClick={() => { setCatEditId(null); setCatEditForm({ name: '', imageUrl: '', imageFile: null }); }}>إلغاء</button>
                    </div>
                </form>
            ) : (
                <form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        let imageUrl = '';
                        if (form.categoryImageFile) {
                            const imgFormData = new FormData();
                            imgFormData.append('Image', form.categoryImageFile);
                            try {
                                const res = await api.post('/Upload', imgFormData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                imageUrl = res.data.url || res.data.imageUrl;
                            } catch {
                                alert('فشل رفع الصورة');
                                return;
                            }
                        }
                        try {
                            await api.post('/ProductCategory', {
                                name: form.categoryName,
                                imageUrl,
                                vendorId: id
                            });
                            setForm(f => ({ ...f, categoryName: '', categoryImageFile: null }));
                            if (document.getElementById('category-image-input')) {
                                document.getElementById('category-image-input').value = '';
                            }
                            fetchProductCategories();
                        } catch {
                            alert('فشل في إضافة الفئة');
                        }
                    }}
                    className="row mb-4"
                >
                    <div className="col-md-3">
                        <input
                            className="form-control mb-2"
                            placeholder="اسم الفئة"
                            value={form.categoryName || ''}
                            onChange={e => setForm(f => ({ ...f, categoryName: e.target.value }))
                            }
                            required
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            type="file"
                            accept="image/*"
                            className="form-control mb-2"
                            id="category-image-input"
                            onChange={e => setForm(f => ({ ...f, categoryImageFile: e.target.files[0] }))
                            }
                        />
                    </div>
                    <div className="col-12">
                        <button className="btn btn-primary">إضافة الفئة</button>
                    </div>
                </form>            )}

            {/* Product Category List with Edit/Delete */}
            <h5 className="mb-3">  قائمة الفئات</h5>
            <table className="table table-bordered text-center mb-4">
                <thead>
                    <tr>
                        <th>الترتيب</th>
                        <th>الاسم</th>
                        <th>الصورة</th>
                        <th>الخيارات</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.isArray(productCategories) && productCategories.length > 0 ? (
                        productCategories.map((c, index) => (
                            <tr key={c.id}>
                                <td>{c.displayOrder}</td>
                                <td>{c.name}</td>
                                <td>{c.imageUrl && (
                                    <BlurredImage src={getImageUrl(c.imageUrl)} alt="تصوير" style={{ maxWidth: 60, maxHeight: 60 }} fallbackUrl={getFallbackUrl(c.imageUrl)} />
                                )}</td>
                                <td>
                                    {index !== 0 && (
                                        <button className="btn btn-sm btn-secondary me-1" onClick={() => handleCatMoveUp(c.id)}>▲</button>
                                    )}
                                    {index !== productCategories.length - 1 && (
                                        <button className="btn btn-sm btn-secondary me-2" onClick={() => handleCatMoveDown(c.id)}>▼</button>
                                    )}
                                    <button className="btn btn-sm btn-primary me-2" onClick={() => handleCatEdit(c)}>تعديل</button>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleCatDelete(c.id)}>حذف</button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="4">لا توجد فئات</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Product Add/Edit Form */}
            <h5 className="mb-3">{editId ? 'تعديل منتج' : 'إضافة منتج'}</h5>
            <form onSubmit={handleSubmit} className="row mb-4">
                <div className="col-md-3">
                    <input
                        className="form-control mb-2"
                        placeholder="اسم المنتج"
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        required
                    />
                </div>
                <div className="col-md-3">
                    <textarea
                        className="form-control mb-2"
                        placeholder="وصف المنتج"
                        rows="2"
                        value={form.description}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="number"
                        className="form-control mb-2"
                        placeholder="السعر (ل.س)"
                        value={form.priceInSYP}
                        onChange={e => setForm(f => ({ ...f, priceInSYP: e.target.value }))}
                        required
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="number"
                        className="form-control mb-2"
                        placeholder="سعر التكلفة"
                        value={form.costPrice}
                        onChange={e => setForm(f => ({ ...f, costPrice: e.target.value }))}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="number"
                        className="form-control mb-2"
                        placeholder="وقت التوصيل (دقيقة)"
                        value={form.deliveryTimeMinutes}
                        onChange={e => setForm(f => ({ ...f, deliveryTimeMinutes: e.target.value }))}
                    />
                    <label className="form-label text-muted small">وقت التوصيل (دقيقة)</label>
                </div>
                <div className="col-md-2">
                    <input
                        type="number"
                        className="form-control mb-2"
                        placeholder="سعر الخصم"
                        value={form.discountPrice}
                        onChange={e => setForm(f => ({ ...f, discountPrice: e.target.value, discountPercentage: e.target.value ? '' : f.discountPercentage }))}
                    />
                    <label className="form-label text-muted small">سعر الخصم (اختياري)</label>
                </div>
                <div className="col-md-2">
                    <input
                        type="number"
                        className="form-control mb-2"
                        placeholder="نسبة الخصم %"
                        min="0"
                        max="99"
                        value={form.discountPercentage}
                        onChange={e => setForm(f => ({ ...f, discountPercentage: e.target.value, discountPrice: e.target.value ? '' : f.discountPrice }))}
                    />
                    <label className="form-label text-muted small">نسبة الخصم (اختياري)</label>
                </div>
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control mb-2"
                        value={form.discountStartDate}
                        onChange={e => setForm(f => ({ ...f, discountStartDate: e.target.value }))}
                    />
                    <label className="form-label text-muted small">بداية الخصم</label>
                </div>
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control mb-2"
                        value={form.discountEndDate}
                        onChange={e => setForm(f => ({ ...f, discountEndDate: e.target.value }))}
                    />
                    <label className="form-label text-muted small">نهاية الخصم</label>
                </div>
                <div className="col-md-2">
                    <select
                        className="form-control mb-2"
                        value={form.selectedCategoryId}
                        onChange={e => setForm(f => ({ ...f, selectedCategoryId: e.target.value }))}
                        required
                    >
                        <option value="">اختر فئة المنتج</option>
                        {Array.isArray(productCategories) && productCategories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-2">
                    <input
                        type="file"
                        accept="image/*"
                        className="form-control mb-2"
                        id="product-image-input"
                        onChange={e => setForm(f => ({ ...f, imageFile: e.target.files[0] }))}
                    />
                </div>
                <div className="col-md-1 d-flex align-items-end">
                    <button className="btn btn-success w-100" type="submit">{editId ? 'حفظ' : 'إضافة'}</button>
                </div>
                {editId && (
                    <div className="col-md-1 d-flex align-items-end">
                        <button
                            type="button"
                            className="btn btn-secondary w-100"
                            onClick={() => {
                                setEditId(null);
                                setClearDiscount(false);
                                setForm({
                                    name: '',
                                    priceInSYP: '',
                                    costPrice: '',
                                    discountPrice: '',
                                    discountPercentage: '',
                                    discountStartDate: '',
                                    discountEndDate: '',
                                    description: '',
                                    imageFile: null,
                                    selectedCategoryId: '',
                                    categoryName: '',
                                    categoryImageFile: null,
                                    deliveryTimeMinutes: ''
                                });
                            }}
                        >
                            إلغاء
                        </button>
                    </div>
                )}
                {editId && (
                    <div className="col-md-2 d-flex align-items-end">
                        <button
                            type="button"
                            className="btn btn-warning w-100"
                            onClick={() => {
                                setForm(f => ({ ...f, discountPrice: '', discountPercentage: '', discountStartDate: '', discountEndDate: '' }));
                                setClearDiscount(true);
                            }}
                        >
                            حذف العرض
                        </button>
                    </div>
                )}
            </form>

            <h5>قائمة المنتجات</h5>
            {loading.products ? (
                <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                    <p className="mt-2">جاري تحميل المنتجات...</p>
                </div>
            ) : ( 
            <table className="table table-bordered text-center">
                <thead>
                    <tr>
                        <th>الاسم</th>
                        <th>الوصف</th>
                        <th>السعر (ل.س)</th>
                        <th>الخصم</th>
                        <th>سعر التكلفة</th>
                        <th>سعر الدولار</th>
                        <th>وقت التوصيل</th>
                        <th>صورة</th>
                        <th>الفئة (اسم وصورة)</th>
                        <th>الحالة</th>
                        <th>خيارات</th>
                    </tr>
                </thead>
                <tbody>
                    {vendor && products.length === 0 ? (
                        <tr>
                            <td colSpan="11">لا توجد منتجات</td>
                        </tr>
                    ) : (
                        products.map((p) => (
                            <tr key={p.id}>
                                <td>{p.name}</td>
                                <td 
                                    style={{
                                        maxWidth: '120px',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'pre-wrap',
                                        fontSize: '12px',
                                        lineHeight: '1.2'
                                    }}
                                >
                                    {p.description || '—'}
                                </td>
                                <td>
                                    {p.hasActiveDiscount && p.finalPrice < p.originalPrice ? (
                                        <>
                                            <div className="fw-bold text-danger">{Number(p.finalPrice || 0).toFixed(0)}</div>
                                            <small className="text-muted text-decoration-line-through">{Number(p.originalPrice || p.priceInSYP || 0).toFixed(0)}</small>
                                        </>
                                    ) : (
                                        <>{p.priceInSYP.toFixed(0)}</>
                                    )}
                                </td>
                                <td>
                                    {p.hasActiveDiscount ? (
                                        <span className="badge bg-warning text-dark">
                                            {Math.round(Number(p.discountPercentage || 0))}%
                                        </span>
                                    ) : (
                                        <small className="text-muted">لا يوجد</small>
                                    )}
                                </td>
                                <td>{p.costPrice?.toFixed(0) || '—'}</td>
                                <td>
                                    {vendor && vendor.dollarExchangeRate > 0
                                        ? (p.priceInSYP / vendor.dollarExchangeRate).toFixed(2)
                                        : '—'}
                                </td>
                                <td>
                                    {p.deliveryTimeMinutes ? `${p.deliveryTimeMinutes} دقيقة` : '—'}
                                </td>
                                <td>
                                    {p.imageUrl && (
                                        <BlurredImage
                                            src={getImageUrl(p.imageUrl)}
                                            alt={p.name}
                                            style={{ width: 60, height: 'auto', borderRadius: 4 }}
                                            fallbackUrl={getFallbackUrl(p.imageUrl)}
                                        />
                                    )}
                                </td>
                                <td>
                                    {p.productCategory?.imageUrl && (
                                        <BlurredImage
                                            src={getImageUrl(p.productCategory.imageUrl)}
                                            alt={p.productCategory?.name}
                                            style={{ width: 40, height: 40, borderRadius: '50%', marginLeft: 6, marginRight: 6 }}
                                            fallbackUrl={getFallbackUrl(p.productCategory.imageUrl)}
                                        />
                                    )}
                                    {p.productCategory?.name || '—'}
                                </td>
                                <td>
                                    <div className="d-flex flex-column align-items-center gap-1">
                                        <span className={`badge ${p.isAvailable !== false ? 'bg-success' : 'bg-danger'}`}>
                                            {p.isAvailable !== false ? 'متاح' : 'غير متاح'}
                                        </span>
                                        <button
                                            className={`btn btn-sm ${p.isAvailable !== false ? 'btn-warning' : 'btn-success'}`}
                                            onClick={() => handleToggleAvailability(p)}
                                            title={p.isAvailable !== false ? 'جعل المنتج غير متاح' : 'جعل المنتج متاح'}
                                        >
                                            {p.isAvailable !== false ? 'جعل المنتج غير متاح' : 'جعل المنتج متاح'}
                                        </button>
                                    </div>
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-warning me-2"
                                        onClick={() => handleEdit(p)}
                                    >
                                        تعديل
                                    </button>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDeleteProduct(p.id)}
                                    >
                                        حذف
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            )}


        </div>
    );
}

export default VendorDetails;
